import { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Loader2, Play, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/utils';

const DATA_ENGINE = () => window._env_?.REACT_APP_DATA_ENGINE_URL || 'http://localhost:8002';

async function startReconciliation(viewId) {
  const r = await fetch(`${DATA_ENGINE()}/api/data-views/${viewId}/reconcile`, { method: 'POST' });
  if (!r.ok) throw new Error(`Failed to start reconciliation: ${r.status}`);
  const json = await r.json();
  return json.data || json;
}

async function fetchStatus(viewId) {
  const r = await fetch(`${DATA_ENGINE()}/api/data-views/${viewId}/reconcile/status`);
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`Failed to fetch status: ${r.status}`);
  const json = await r.json();
  return json.data || json;
}

async function fetchFailures(viewId) {
  const r = await fetch(`${DATA_ENGINE()}/api/data-views/${viewId}/reconcile/failures`);
  if (r.status === 404) return [];
  if (!r.ok) throw new Error(`Failed to fetch failures: ${r.status}`);
  const json = await r.json();
  return json.data || [];
}

function VarianceDetails({ variance }) {
  if (!variance || typeof variance !== 'object') return <span className="text-zinc-400">--</span>;

  const entries = Object.entries(variance);
  if (entries.length === 0) return <span className="text-zinc-400">--</span>;

  return (
    <div className="space-y-1">
      {entries.map(([field, detail]) => {
        const cubeVal = detail?.cube_value ?? detail?.expected ?? '--';
        const qbVal = detail?.qb_value ?? detail?.actual ?? '--';
        const diff = detail?.diff ?? detail?.difference ?? '--';
        return (
          <div key={field} className="text-[11px] leading-tight">
            <span className="font-medium text-zinc-700">{field}</span>
            <span className="text-zinc-400 ml-1">
              cube: <span className="text-zinc-600">{cubeVal}</span>
              {' / '}
              qb: <span className="text-zinc-600">{qbVal}</span>
              {' / '}
              diff: <span className={cn(
                'font-medium',
                diff !== 0 && diff !== '--' ? 'text-red-600' : 'text-zinc-500'
              )}>{typeof diff === 'number' ? diff.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : diff}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function ReconciliationPanel({ viewId, reportType }) {
  const [status, setStatus] = useState(null);
  const [failures, setFailures] = useState([]);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [failuresOpen, setFailuresOpen] = useState(true);
  const pollRef = useRef(null);
  const isCustom = reportType === 'custom' || !reportType;

  const isRunning = status?.status === 'running' || status?.status === 'pending';
  const isDone = status?.status === 'completed' || status?.status === 'done';
  const total = status?.total_companies || 0;
  const completed = status?.completed_count || 0;
  const passed = status?.passed_count || 0;
  const failed = status?.failed_count || 0;
  const errored = status?.errored_count || 0;
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Poll status while running
  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await fetchStatus(viewId);
        if (s) setStatus(s);
        if (s && s.status !== 'running' && s.status !== 'pending') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          // Fetch failures when done
          const f = await fetchFailures(viewId);
          setFailures(f);
        }
      } catch {
        // ignore polling errors
      }
    }, 3000);
  }, [viewId]);

  // Load initial status
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await fetchStatus(viewId);
        if (!cancelled && s) {
          setStatus(s);
          if (s.status === 'running' || s.status === 'pending') {
            startPolling();
          } else {
            const f = await fetchFailures(viewId);
            if (!cancelled) setFailures(f);
          }
        }
      } catch {
        // no previous reconciliation — that's ok
      }
    })();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [viewId, startPolling]);

  const handleRun = async () => {
    setStarting(true);
    setError(null);
    setFailures([]);
    try {
      const result = await startReconciliation(viewId);
      setStatus(result);
      startPolling();
    } catch (err) {
      setError(err.message);
    } finally {
      setStarting(false);
    }
  };

  // Don't render for custom views (guard after all hooks)
  if (isCustom) return null;

  return (
    <div className="border-t border-zinc-200 bg-zinc-50/50">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-2.5">
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-zinc-700">Reconciliation</span>

          {/* Status summary badges */}
          {isDone && (
            <div className="flex items-center gap-2">
              {passed > 0 && (
                <span className="flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  <CheckCircle2 size={11} /> {passed} passed
                </span>
              )}
              {failed > 0 && (
                <span className="flex items-center gap-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                  <XCircle size={11} /> {failed} failed
                </span>
              )}
              {errored > 0 && (
                <span className="flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                  <AlertTriangle size={11} /> {errored} errored
                </span>
              )}
            </div>
          )}

          {isRunning && (
            <div className="flex items-center gap-2">
              <Loader2 size={12} className="animate-spin text-blue-500" />
              <span className="text-[11px] text-zinc-500">
                {completed}/{total} companies ({progress}%)
              </span>
              {passed > 0 && <span className="text-[11px] text-emerald-600">{passed} passed</span>}
              {failed > 0 && <span className="text-[11px] text-red-600">{failed} failed</span>}
              {errored > 0 && <span className="text-[11px] text-amber-600">{errored} errored</span>}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Toggle failures */}
          {failures.length > 0 && (
            <button
              onClick={() => setFailuresOpen(v => !v)}
              className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-700 transition-colors"
            >
              {failuresOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {failures.length} failure{failures.length !== 1 ? 's' : ''}
            </button>
          )}

          {/* Run button */}
          <button
            onClick={handleRun}
            disabled={starting || isRunning}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
          >
            {starting || isRunning ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Play size={12} />
            )}
            {starting ? 'Starting...' : isRunning ? 'Running...' : 'Run Reconciliation'}
          </button>
        </div>
      </div>

      {/* Progress bar while running */}
      {isRunning && total > 0 && (
        <div className="px-4 pb-2">
          <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mx-4 mb-2 px-3 py-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {/* Failures table */}
      {failuresOpen && failures.length > 0 && (
        <div className="mx-4 mb-3 border border-zinc-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-zinc-100">
              <tr>
                <th className="text-left px-3 py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">Company</th>
                <th className="text-left px-3 py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-3 py-2 text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">Variance Details</th>
              </tr>
            </thead>
            <tbody>
              {failures.map((f, i) => (
                <tr key={f.rf_id || i} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-3 py-2 text-zinc-700 font-medium whitespace-nowrap">{f.rf_id}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={cn(
                      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
                      f.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
                    )}>
                      {f.status === 'failed' ? <XCircle size={10} /> : <AlertTriangle size={10} />}
                      {f.status}
                    </span>
                    {f.error_message && (
                      <div className="text-[10px] text-zinc-400 mt-0.5">{f.error_message}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <VarianceDetails variance={f.variance} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
